import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import serviceToContentMap, { getSynonym } from '../../../../routes/content/ContentMap';
import { colors } from '../../../../constants/colors';
import { fontSize, fontFamily } from '../../../../constants/text';
import { isEmptyObject, safeGet } from '../../../../core/util/object';
import PanelCard from '../../PanelCards/components/PanelCard';
import {
  CDP_TOUCH_SERVICES,
  CDP_TOUCH_SHOW_MORE_SERVICES,
} from '../../../../core/analytics/events';
import { analyticsTrackEvent } from '../../../../core/analytics';
import { BASE_URL } from '../../../../config';
import { isTraditionalLocation } from '../../../../core/util/location';
import {
  getSanitizedCity,
  getServiceDirectoryUrl,
  getStateDirectoryUrl,
} from '../../../../core/util/seo';
import { getCustomization, SERVICES } from '../../util/customization';

const CATEGORY_VACCINATIONS = 'Vaccinations';
const CATEGORY_LABS_AND_TESTS = 'Labs and Tests';

const Root = styled.div`
  font-family: ${fontFamily.book};
`;

const ServiceNameContainer = styled.span`
  height: 30px;
  background-color: ${colors.fatherJohnMisty};
  border-radius: 4px;
  text-align: center;
  padding: 5px;
  line-height: 40px;
`;

const Link = styled.a`
  text-decoration: none;
  color: ${colors.blueJayWay};
  font-size: ${fontSize.mediumSmall};
  &:hover {
    text-decoration: underline;
  }
  vertical-align: middle;
  padding-left: 5px;
  padding-right: 5px;
  line-height: 39px;
`;

const SectionsList = styled.ul`
  margin: 0;
  padding: 0;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const SectionsListItem = styled.li`
  list-style-type: none;
  display: inline;
  margin-right: 10px;
`;

const CategoryTitle = styled.h3`
  font-size: ${fontSize.mediumSmall};
  color: ${colors.squid};
`;

const StaticBubble = styled.span`
  color: ${colors.blueMonday};
  font-size: ${fontSize.mediumSmall};
  vertical-align: middle;
  padding-left: 5px;
  padding-right: 5px;
  line-height: 39px;
`;

const ViewAllServicesCta = styled.button`
  border: 0;
  color: ${colors.blueJayWay};
  font-size: ${fontSize.mediumSmall};
  margin: 10px 0;
  cursor: pointer;
  background-color: transparent;
`;

class ServicesList extends PureComponent {
  constructor(props) {
    super(props);
    const { sessionTrackingProperties } = props;
    const userAgent = safeGet(sessionTrackingProperties)('userAgent');
    this.servicesAreLinkable = getCustomization(SERVICES, {
      userAgent,
      location: this.props.location,
    }).shouldBeLinkable;
  }

  state = {
    expanded: false,
  };

  getTopCategoryNames = locationServices => {
    if (
      locationServices.hasOwnProperty(CATEGORY_VACCINATIONS) &&
      locationServices.hasOwnProperty(CATEGORY_LABS_AND_TESTS)
    ) {
      return [CATEGORY_VACCINATIONS, CATEGORY_LABS_AND_TESTS];
    }

    if (Object.keys(locationServices).length === 1) {
      return [Object.keys(locationServices)[0]];
    }

    return [Object.keys(locationServices)[0], Object.keys(locationServices)[1]];
  };

  getBottomCategoryNames = (locationServices, topCategoryNames) => {
    const categoryNames = Object.keys(locationServices);
    topCategoryNames.forEach(topCategory =>
      categoryNames.splice(categoryNames.indexOf(topCategory), 1)
    );
    return categoryNames;
  };

  getCategory = (category, locationServices, isReadOnly) => (
    <div key={category}>
      <CategoryTitle data-testid={`categoryTitle-${category}`}>{category}</CategoryTitle>
      <SectionsList>
        {locationServices[category].map(
          ({ display_name: service, slug, is_popular: isPopular }, index) => (
            <SectionsListItem
              index={index}
              key={service}
              itemScope
              itemType={
                category !== CATEGORY_LABS_AND_TESTS
                  ? 'http://schema.org/MedicalProcedure'
                  : 'http://schema.org/MedicalTest'
              }
              itemProp="availableService"
            >
              <ServiceNameContainer itemProp="name">
                {this.renderService(service, slug, isPopular, isReadOnly)}
              </ServiceNameContainer>
            </SectionsListItem>
          )
        )}
      </SectionsList>
    </div>
  );

  getLocationServices = (categories, locationServices, isReadOnly = false) =>
    categories.map(category => this.getCategory(category, locationServices, isReadOnly));

  handleClick = service => {
    const {
      location: { id, uclId },
    } = this.props;
    analyticsTrackEvent(CDP_TOUCH_SERVICES, { locationId: id, service, uclId });
  };

  expand = () => {
    const {
      location: { id, uclId },
    } = this.props;
    analyticsTrackEvent(CDP_TOUCH_SHOW_MORE_SERVICES, {
      locationId: id,
      uclId,
    });
    this.setState({ expanded: true });
  };

  shouldRender = () => {
    const { location } = this.props;
    return !isEmptyObject(location && location.servicesObj);
  };

  renderService = (service, slug, isPopular, isReadOnly) => {
    const {
      location,
      location: { state, city },
    } = this.props;

    if (isReadOnly || !this.servicesAreLinkable) {
      return <StaticBubble>{getSynonym(service)}</StaticBubble>;
    }

    if (serviceToContentMap.has(service)) {
      return (
        <Link
          onClick={() => this.handleClick(service)}
          href={`${BASE_URL}${serviceToContentMap.get(service)}`}
          itemProp="url"
          content={`${BASE_URL}${serviceToContentMap.get(service)}`}
        >
          {getSynonym(service)}
        </Link>
      );
    }

    if (isTraditionalLocation(location) && isPopular && slug) {
      const url = getServiceDirectoryUrl(state, city, slug);

      return (
        <Link
          onClick={() => this.handleClick(service)}
          href={`${BASE_URL}${url}`}
          itemProp="url"
          content={`${BASE_URL}${url}`}
        >
          {getSynonym(service)}
        </Link>
      );
    }

    return <StaticBubble>{getSynonym(service)}</StaticBubble>;
  };

  render = () => {
    if (!this.shouldRender()) return null;

    const { enableCollapsing, defaultExpanded, isReadOnly } = this.props;
    const locationServices = this.props.location.servicesObj;
    const servicesCount = Object.keys(locationServices).length + 1;
    const { expanded } = this.state;

    const topCategoryNames = this.getTopCategoryNames(locationServices);
    const bottomCategoryNames = this.getBottomCategoryNames(locationServices, topCategoryNames);

    return (
      <PanelCard title="Services" {...{ enableCollapsing, defaultExpanded }}>
        <Root>
          {this.getLocationServices(topCategoryNames, locationServices, isReadOnly)}
          {!expanded && servicesCount > 2 && (
            <ViewAllServicesCta data-testid="viewAllServices" onClick={this.expand}>
              View all services...
            </ViewAllServicesCta>
          )}
          {expanded && this.getLocationServices(bottomCategoryNames, locationServices, isReadOnly)}
        </Root>
      </PanelCard>
    );
  };
}

ServicesList.propTypes = {
  location: PropTypes.object,
  enableCollapsing: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  sessionTrackingProperties: PropTypes.object,
};

export default ServicesList;
